import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { getSaleInvoiceTotal } from "../../../services/saleInvoiceContract";
import "./InvoiceEditor.css";
import InvoiceProductPicker from "./InvoiceProductPicker";
import InvoiceServicePicker from "./InvoiceServicePicker";
import saleApi from "../../../services/sale.service";

const minor = (value) => {
  if (value && typeof value === "object") return Number(value.amount_minor) || 0;
  return Math.round((Number(value) || 0) * 100);
};
const decimal = (value) => (minor(value) / 100).toFixed(2);
const asMinor = (value) => Math.round(Number(value) * 100);
const offlineMethods = ["Cash", "CreditCard", "BankTransfer"];
const paymentRows = (invoice) => (invoice?.payments || invoice?.lastpayment || [])
  .filter((payment) => !["failed", "declined", "pending", "authorized", "refunded"].includes(String(payment.status || "").toLowerCase()));
const editableMethod = (method) => {
  const normalized = String(method || "").replace(/\s+/g, "");
  return offlineMethods.includes(normalized) ? normalized : "";
};
const latestPayment = (invoice) => paymentRows(invoice).slice(-1)[0];

const initialDraft = (invoice) => ({
  lines: (invoice?.line_items || []).map((line) => ({
    id: line.id,
    itemType: line.item?.type || line.item_type || "Custom",
    name: line.name || line.description || "",
    quantity: String(line.quantity || 1),
    price: decimal(line.unit_amount_minor !== undefined ? { amount_minor: line.unit_amount_minor } : line.unit_amount),
  })),
  discount_code: invoice?.discount_code ?? null,
  discount: decimal(invoice?.discount_minor !== undefined ? { amount_minor: invoice.discount_minor } : invoice?.discount),
  tax: decimal(invoice?.tax_minor !== undefined ? { amount_minor: invoice.tax_minor } : invoice?.tax),
  notes: invoice?.notes || invoice?.description || "",
  invoicedate: typeof invoice?.invoicedate === "string" ? invoice.invoicedate.slice(0, 10) : "",
  paidby: editableMethod(latestPayment(invoice)?.paidby || latestPayment(invoice)?.paid_by),
  paidby_status: latestPayment(invoice)?.paidby_status || "completed",
});

const InvoiceEditor = ({ invoice, canEdit, startEditing = false, paymentPanelId, onSave, onChanged, loadProducts, loadServices, loadDiscountPreview = saleApi.update }) => {
  const [editing, setEditing] = useState(startEditing);
  const [draft, setDraft] = useState(() => initialDraft(invoice));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [paymentPanel, setPaymentPanel] = useState(null);
  const [productPicker, setProductPicker] = useState(null);
  const [discountPreview, setDiscountPreview] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const previewPayload = JSON.stringify({
    id: invoice?.id, preview: true,
    line_items: draft.lines.map((line) => ({
      ...(line.id ? { id: line.id } : {}),
      ...(line.product_id ? { product_id: line.product_id } : {}),
      ...(line.service_id ? { service_id: line.service_id } : {}),
      name: line.name.trim(), quantity: Number(line.quantity), unit_amount_minor: asMinor(line.price),
    })),
    discount_minor: asMinor(draft.discount), tax_minor: asMinor(draft.tax), notes: draft.notes,
    ...(draft.discount_code !== null ? { discount_code: draft.discount_code } : {}),
  });
  const currentPreview = discountPreview?.key === previewPayload ? discountPreview.data : null;
  useEffect(() => {
    if (!editing || !canEdit || !invoice?.id) return;
    let active = true;
    setDiscountError("");
    Promise.resolve().then(() => loadDiscountPreview(JSON.parse(previewPayload))).then((response) => {
      if (active) setDiscountPreview({ key: previewPayload, data: response?.data || response });
    }).catch((failure) => {
      if (active) setDiscountError(failure?.response?.data?.error?.message || "Unable to load applicable discount codes. Check the invoice details and try again.");
    });
    return () => { active = false; };
  }, [editing, canEdit, invoice?.id, previewPayload, loadDiscountPreview]);

  useEffect(() => {
    setPaymentPanel(paymentPanelId && editing ? document.getElementById(paymentPanelId) : null);
  }, [paymentPanelId, editing]);

  if (!canEdit || !invoice?.id) return null;
  const setField = (key, value) => setDraft((previous) => ({ ...previous, [key]: value }));
  const setLine = (index, key, value) => setDraft((previous) => ({
    ...previous,
    lines: previous.lines.map((line, position) => position === index ? { ...line, [key]: value } : line),
  }));
  const chooseProduct = (product) => {
    setDraft((previous) => {
      const selection = { product_id: product.id, itemType: "Product", name: product.name };
      return { ...previous, lines: productPicker.index === null
        ? [...previous.lines, { ...selection, quantity: "1", price: decimal(product.retail_price) }]
        : previous.lines.map((line, index) => index === productPicker.index ? { ...line, ...selection } : line) };
    });
    setProductPicker(null);
  };
  const chooseService = (service) => {
    setDraft((previous) => ({ ...previous, lines: [...previous.lines, {
      service_id: service.id, itemType: "Service", name: service.name, quantity: "1", price: decimal(service.price),
    }] }));
    setProductPicker(null);
  };
  const saveDate = async () => {
    if (!draft.invoicedate || saving) return;
    setError("");
    setSaving(true);
    try {
      const result = await onSave({ id: invoice.id, invoicedate: draft.invoicedate });
      await onChanged(result);
    } catch (failure) {
      setError(failure?.userMessage || failure?.response?.data?.error?.message || failure?.message?.error?.message || failure?.message || "Unable to save invoice date.");
    } finally {
      setSaving(false);
    }
  };
  const savePaymentMethod = async () => {
    if (saving || !offlineMethods.includes(draft.paidby)) return;
    setError("");
    setSaving(true);
    try {
      const result = await onSave({
        id: invoice.id,
        paidby: draft.paidby,
        ...(draft.paidby === "BankTransfer" ? { paidby_status: draft.paidby_status } : {}),
      });
      await onChanged(result);
    } catch (failure) {
      setError(failure?.userMessage || failure?.response?.data?.error?.message || failure?.message?.error?.message || failure?.message || "Unable to save payment method.");
    } finally {
      setSaving(false);
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (saving || productPicker || (draft.discount_code !== null && !currentPreview)) return;
    const discount = asMinor(draft.discount);
    const tax = asMinor(draft.tax);
    const lines = draft.lines.map((line) => ({
      ...(line.id ? { id: line.id } : {}),
      ...(line.product_id ? { product_id: line.product_id } : {}),
      ...(line.service_id ? { service_id: line.service_id } : {}),
      name: line.name.trim(),
      quantity: Number(line.quantity),
      unit_amount_minor: asMinor(line.price),
    }));
    if (!lines.length || lines.some((line) => !line.name || !Number.isInteger(line.quantity) || line.quantity < 1 || !Number.isInteger(line.unit_amount_minor) || line.unit_amount_minor < 0) || !Number.isInteger(discount) || discount < 0 || !Number.isInteger(tax) || tax < 0) {
      setError("Enter valid line items, discount, and tax.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const result = await onSave({
        id: invoice.id,
        line_items: lines,
        ...(invoice.status === "Paid" && draft.discount_code === null ? { settle_balance: true } : {}),
        ...(draft.discount_code !== null ? { discount_code: draft.discount_code } : {}),
        discount_minor: discount,
        tax_minor: tax,
        notes: draft.notes,
      });
      await onChanged(result);
      setEditing(false);
    } catch (failure) {
      setError(failure?.userMessage || failure?.response?.data?.error?.message || failure?.message?.error?.message || failure?.message || "Unable to save invoice.");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) return <button type="button" className="btn btn-link p-0" onClick={() => {
    setDraft(initialDraft(invoice));
    setError("");
    setEditing(true);
  }}>Edit invoice</button>;

  const currency = invoice.currency || invoice.total?.currency || "AUD";
  const client = invoice.client || {};
  const clientName = [client.first_name, client.last_name].filter(Boolean).join(" ");
  const paidMethods = [...new Set(paymentRows(invoice)
    .map((payment) => payment.paidby || payment.paid_by)
    .filter(Boolean))];
  const currentEditableMethod = editableMethod(latestPayment(invoice)?.paidby || latestPayment(invoice)?.paid_by);
  const paymentControls = currentEditableMethod && <div className="invoice-editor-payment mb-3">
    <fieldset>
      <legend className="invoice-editor-caption">Payment method</legend>
      <label htmlFor="invoice-edit-paidby" className="visually-hidden">Payment method</label>
      <select id="invoice-edit-paidby" className="form-select visually-hidden" value={draft.paidby} onChange={(event) => setField("paidby", event.target.value)}>
        <option value="Cash">Cash</option><option value="CreditCard">Credit Card</option><option value="BankTransfer">Bank Transfer</option>
      </select>
      <div className="invoice-editor-payment-tiles">
        {[["Cash", "Cash", "💵"], ["CreditCard", "Credit Card", "▰"], ["BankTransfer", "Bank Transfer", "▤"]].map(([method, label, icon]) =>
          <button key={method} type="button" className={`invoice-editor-payment-tile${draft.paidby === method ? " is-selected" : ""}`}
            aria-pressed={draft.paidby === method} onClick={() => setField("paidby", method)}>
            <span aria-hidden="true" className="invoice-editor-payment-icon">{icon}</span>{label}
          </button>)}
      </div>
    </fieldset>
    {draft.paidby === "BankTransfer" && <><label htmlFor="invoice-edit-bank-status">Bank transfer status</label><select id="invoice-edit-bank-status" className="form-select" value={draft.paidby_status} onChange={(event) => setField("paidby_status", event.target.value)}><option value="completed">Completed</option><option value="in_progress">In progress</option></select></>}
    <button type="button" className="btn btn-outline-primary mt-2" disabled={saving || (draft.paidby === currentEditableMethod && (draft.paidby !== "BankTransfer" || draft.paidby_status === initialDraft(invoice).paidby_status))} onClick={savePaymentMethod}>Save payment method</button>
  </div>;

  return <form onSubmit={submit} className="invoice-editor mt-3" aria-label="Edit invoice">
    <div className="invoice-editor-heading mb-3">
      <h2>Edit Sale</h2>
      <p>Update invoice date and amounts</p>
    </div>
    <div className="invoice-editor-context mb-3">
      <h3>{`Tax Invoice ${invoice.invoice_number || `#${invoice.id}`}`}</h3>
      {invoice.status && <div><span className="invoice-editor-caption">Status</span><span className="badge paid ms-2">{invoice.status}</span></div>}
      <div className="invoice-editor-date"><label htmlFor="invoice-edit-date">Date of invoice</label><div className="d-flex align-items-center gap-2"><input id="invoice-edit-date" className="form-control" type="date" value={draft.invoicedate} onChange={(event) => setField("invoicedate", event.target.value)} /><button type="button" className="btn btn-outline-primary" disabled={saving || !draft.invoicedate || draft.invoicedate === initialDraft(invoice).invoicedate} onClick={saveDate}>Save invoice date</button></div></div>
      {clientName && <div className="invoice-editor-client"><span className="invoice-editor-caption">Invoice to:</span><strong>{clientName}</strong></div>}
    </div>
    {invoice.status === "Paid" && draft.discount_code === null && <p>Any increase is recorded as paid using the original payment method.</p>}
    <div className="invoice-editor-caption">Invoice items</div>
    {draft.lines.map((line, index) => <div key={line.id ?? `new-${index}`} className="invoice-editor-line mt-2">
      <div className="invoice-editor-line-name"><label htmlFor={`invoice-line-${index}-name`}>Line {index + 1} name</label><input id={`invoice-line-${index}-name`} className="form-control" required value={line.name} onChange={(event) => setLine(index, "name", event.target.value)} />
        {String(line.itemType).toLowerCase() === "custom" && <button type="button" className="btn btn-link p-0" disabled={saving}
          aria-label={`Choose product for line ${index + 1}`} onClick={() => setProductPicker({ index })}>Choose catalogue product</button>}
        {line.itemType === "Product" && <small className="d-block text-muted">Catalogue product</small>}
      </div>
      <div className="invoice-editor-line-quantity"><label htmlFor={`invoice-line-${index}-quantity`}>Line {index + 1} quantity</label><input id={`invoice-line-${index}-quantity`} className="form-control" type="number" min="1" step="1" required value={line.quantity} onChange={(event) => setLine(index, "quantity", event.target.value)} /></div>
      <div className="invoice-editor-line-price"><label htmlFor={`invoice-line-${index}-price`}>Line {index + 1} unit price</label><input id={`invoice-line-${index}-price`} className="form-control" type="number" min="0" step="0.01" required value={line.price} onChange={(event) => setLine(index, "price", event.target.value)} /></div>
      <div className="d-flex align-items-end"><button type="button" className="btn btn-outline-danger" aria-label={`Remove line ${index + 1}`} disabled={saving} onClick={() => { setProductPicker(null); setDraft((previous) => ({ ...previous, lines: previous.lines.filter((_, position) => position !== index) })); }}>Remove</button></div>
    </div>)}
    <button type="button" className="btn btn-link" disabled={saving} onClick={() => setProductPicker({ index: null })}>Add product</button>
    <button type="button" className="btn btn-link" disabled={saving} onClick={() => setDraft((previous) => ({ ...previous, lines: [...previous.lines, { itemType: "Custom", name: "", quantity: "1", price: "0.00" }] }))}>Add custom line</button>
    <button type="button" className="btn btn-link" disabled={saving} onClick={() => setProductPicker({ kind: "service" })}>Add service</button>
    {productPicker?.kind === "service" && <InvoiceServicePicker loadServices={loadServices} onSelect={chooseService} onCancel={() => setProductPicker(null)} />}
    {productPicker && productPicker.kind !== "service" && <InvoiceProductPicker key={productPicker.index ?? "new"} loadProducts={loadProducts}
      onSelect={chooseProduct} onCancel={() => setProductPicker(null)} />}
    <div><label htmlFor="invoice-edit-code">Discount code</label>
      <select id="invoice-edit-code" className="form-select" value={draft.discount_code ?? ""}
        disabled={saving || (!currentPreview && !discountError)} onChange={(event) => setField("discount_code", event.target.value)}>
        <option value="">No discount code</option>
        {draft.discount_code && !(currentPreview?.discount_options || []).some((item) => item.code === draft.discount_code) &&
          <option value={draft.discount_code}>{draft.discount_code} (saved)</option>}
        {(currentPreview?.discount_options || []).map((item) => <option key={item.code} value={item.code}>{item.code}</option>)}
      </select>
      {discountError && <p role="alert">{discountError}</p>}
    </div>
    <div><label htmlFor="invoice-edit-discount">Invoice discount</label><input id="invoice-edit-discount" className="form-control" type="number" min="0" step="0.01"
      readOnly value={draft.discount_code !== null && currentPreview ? decimal(currentPreview.discount) : draft.discount} /></div>
    <div><label htmlFor="invoice-edit-tax">Invoice tax</label><input id="invoice-edit-tax" className="form-control" type="number" min="0" step="0.01"
      value={draft.tax}
      onChange={(event) => setField("tax", event.target.value)} /></div>
    <div><label htmlFor="invoice-edit-notes">Invoice notes</label><textarea id="invoice-edit-notes" className="form-control" value={draft.notes} onChange={(event) => setField("notes", event.target.value)} /></div>
    <div className="invoice-editor-summary">
      <div><span>{`Total ${currency}`}</span><strong>{new Intl.NumberFormat("en-AU", { style: "currency", currency }).format(getSaleInvoiceTotal(currentPreview || invoice))}</strong></div>
      {currentPreview?.refund_due?.amount_minor > 0 && <p>Refund due: {new Intl.NumberFormat("en-AU", { style: "currency", currency }).format(currentPreview.refund_due.amount_minor / 100)}. Issue this refund separately; saving does not refund or change the original payment.</p>}
      {draft.discount_code !== null && currentPreview?.balance?.amount_minor > 0 && <p>Balance due: {new Intl.NumberFormat("en-AU", { style: "currency", currency }).format(currentPreview.balance.amount_minor / 100)}. Collect this payment separately.</p>}
      {paidMethods.length > 0 && <div><span>Recorded payment method</span><span>{paidMethods.join(" + ")}</span></div>}
    </div>
    {paymentPanel ? createPortal(paymentControls, paymentPanel) : paymentControls}
    {error && <p role="alert" className="text-danger">{String(error)}</p>}
    <button type="submit" className="btn btn-primary" disabled={saving || !!productPicker || (draft.discount_code !== null && !currentPreview)}>{saving ? "Saving invoice…" : "Save invoice"}</button>
    <button type="button" className="btn btn-link" disabled={saving} onClick={() => { setProductPicker(null); setEditing(false); }}>Cancel</button>
  </form>;
};

InvoiceEditor.propTypes = {
  invoice: PropTypes.object,
  canEdit: PropTypes.bool.isRequired,
  startEditing: PropTypes.bool,
  paymentPanelId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  loadProducts: PropTypes.func,
  loadServices: PropTypes.func,
  loadDiscountPreview: PropTypes.func,
};

export default InvoiceEditor;
